import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'framer-motion'
import { Landmark, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useState } from 'react'

export const Route = createFileRoute('/bank-automatisk-betalning')({
  component: RouteComponent,
})

type Step = { text: string; sub?: string[] }

const banks: { name: string; steps: Step[]; note?: string }[] = [
  {
    name: 'Swedbank & Sparbankerna',
    note: 'Observera att vissa Sparbanker inte tillåter att stående betalningar till plusgiro sätts upp via mobilappen eller internetbanken. Kontakta i så fall din bank via telefon.',
    steps: [
      { text: 'Logga in på Internetbanken (swedbank.se) eller i appen.' },
      { text: 'Gå till Betala & överföra → Betalningar.' },
      {
        text: 'Fyll i ett nytt betalningsuppdrag med:',
        sub: ['Plusgiro: 51 34 31-7', 'Belopp'],
      },
      {
        text: 'Innan du bekräftar ser du ett alternativ för "Upprepa betalning" eller "Stående betalning" — välj det och ställ in:',
        sub: ['Hur ofta (månadsvis)', 'Startdatum', 'Eventuellt slutdatum'],
      },
      { text: 'Signera med BankID.' },
    ],
  },
  {
    name: 'Länsförsäkringar',
    steps: [
      { text: 'Logga in på Mina sidor (lansforsakringar.se eller appen).' },
      { text: 'Gå till Betala & överföra → Betalningar.' },
      {
        text: 'Välj Ny betalning och fyll i:',
        sub: ['Plusgiro: 51 34 31-7', 'Beloppet', 'Vilket datum du vill att den första betalningen ska gå'],
      },
      {
        text: 'När du fyllt i uppgifterna hittar du ett alternativ för att göra betalningen återkommande/stående — välj månadsvis och ange eventuellt slutdatum (eller lämna öppet).',
      },
      { text: 'Signera med BankID.' },
    ],
  },
  {
    name: 'SEB',
    steps: [
      { text: 'Logga in på internetbanken (seb.se) eller i appen.' },
      { text: 'Gå till Betala & överföra → Betalningar.' },
      {
        text: 'Fyll i:',
        sub: ['Plusgiro: 51 34 31-7', 'Belopp'],
      },
      { text: 'Välj "Återkommande betalning" och ställ in månadsvis, startdatum och eventuellt slutdatum.' },
      { text: 'Signera med BankID.' },
    ],
  },
  {
    name: 'Handelsbanken',
    steps: [
      { text: 'Logga in på internetbanken (handelsbanken.se) eller i appen.' },
      { text: 'Gå till Betala → Betalningar.' },
      {
        text: 'Fyll i:',
        sub: ['Plusgiro: 51 34 31-7', 'Belopp'],
      },
      { text: 'Välj "Stående betalningsuppdrag" och ställ in månadsvis, startdatum och eventuellt slutdatum.' },
      { text: 'Signera med BankID eller säkerhetsdosa.' },
    ],
  },
  {
    name: 'Nordea',
    steps: [
      { text: 'Logga in på internetbanken (nordea.se) eller i appen.' },
      { text: 'Gå till Betala → Betalningar.' },
      {
        text: 'Fyll i ett nytt betalningsuppdrag med:',
        sub: ['Plusgiro: 51 34 31-7', 'Belopp'],
      },
      {
        text: 'Välj alternativet "Återkommande betalning" och ställ in:',
        sub: ['Månadsvis', 'Startdatum', 'Eventuellt slutdatum'],
      },
      { text: 'Signera med BankID.' },
    ],
  },
]

function BankPanel({ bank }: { bank: (typeof banks)[number] }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="font-semibold text-gray-900">{bank.name}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-5 h-5 text-gray-500" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <ol className="px-5 pb-5 space-y-3 text-gray-700 list-decimal list-inside">
              {bank.steps.map((step, i) => (
                <li key={i}>
                  {step.text}
                  {step.sub && (
                    <ul className="mt-1 ml-5 space-y-1 list-disc list-inside text-gray-600 text-sm">
                      {step.sub.map((s, j) => (
                        <li key={j}>{s}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
            {bank.note && (
              <p className="mx-5 -mt-1 mb-5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                {bank.note}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function RouteComponent() {
  const navigate = useNavigate()

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto max-w-2xl p-6"
    >
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
        >
          <Landmark className="w-20 h-20 text-green-500 mx-auto mb-4" />
        </motion.div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Automatisk bankbetalning</h1>
        <p className="text-gray-600">Ett kostnadsfritt alternativ till kortbetalning</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-lg p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Varför bankbetalning?</h2>
        <p className="text-gray-700">
          När du betalar med kort dras en liten avgift från varje transaktion, pengar som annars
          hade gått direkt till vårt arbete. Genom att istället sätta upp en automatisk
          bankbetalning går hela ditt bidrag till KRIS Stödförening.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-8"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Välj din bank</h2>
        <div className="space-y-3">
          {banks.map((bank) => (
            <BankPanel key={bank.name} bank={bank} />
          ))}
        </div>
        <p className="mt-4 text-sm text-gray-500">
          Bankernas gränssnitt kan förändras över tid. Om stegen inte stämmer, kontakta din bank via telefon så hjälper de dig att sätta upp en stående betalning.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center space-y-4"
      >
        <p className="text-sm text-gray-500">
          Har du frågor? Kontakta oss på{' '}
          <a href="mailto:stodforening@kris.se" className="text-green-600 hover:underline">
            stodforening@kris.se
          </a>
        </p>
        <Button
          onClick={() => navigate({ to: '/' })}
          className="hover:bg-green-600 transition-colors"
        >
          Tillbaka till startsidan
        </Button>
      </motion.div>
    </motion.div>
  )
}
